import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./lib/auth-context";
import { BrandingProvider } from "./lib/branding-context";
import { ThemeProvider } from "./components/ThemeProvider";
import { ToastProvider } from "./components/ui/Toast";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { DashboardSkeleton } from "./components/Skeleton";
import { DashboardLayout } from "./components/DashboardLayout";

// Eagerly loaded pages (critical path)
import Home from "./pages/Home";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import AgentChat from "./pages/AgentChat";
import BookingWizard from "./pages/BookingWizard";
import ChatPage from "./pages/ChatPage";
import SmsTerms from "./pages/SmsTerms";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";

// Lazy loaded pages (code-split for smaller initial bundle)
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminTenants = lazy(() => import("./pages/admin/AdminTenants"));
const AdminChatbotUsage = lazy(() => import("./pages/admin/AdminChatbotUsage"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminMetaLeads = lazy(() => import("./pages/admin/AdminMetaLeads"));
const Settings = lazy(() => import("./pages/Settings"));
const Services = lazy(() => import("./pages/Services"));
const Providers = lazy(() => import("./pages/Providers"));
const Clients = lazy(() => import("./pages/Clients"));
const ClientProfile = lazy(() => import("./pages/ClientProfile"));
const Payments = lazy(() => import("./pages/Payments"));
const Forms = lazy(() => import("./pages/Forms"));
const Documents = lazy(() => import("./pages/Documents"));
const PublicForm = lazy(() => import("./pages/PublicForm"));
const PublicDocument = lazy(() => import("./pages/PublicDocument"));
const PublicRating = lazy(() => import("./pages/PublicRating"));
const ReviewGate = lazy(() => import("./pages/ReviewGate"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const ChatbotAdmin = lazy(() => import("./pages/ChatbotAdmin"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const CampaignDetail = lazy(() => import("./pages/CampaignDetail"));
const LeadProfile = lazy(() => import("./pages/LeadProfile"));
const EmailBlasts = lazy(() => import("./pages/EmailBlasts"));
const EmailBlastEditor = lazy(() => import("./pages/EmailBlastEditor"));
const PromoPage = lazy(() => import("./pages/PromoPage"));
const ManageAppointment = lazy(() => import("./pages/ManageAppointment"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// Virtual Receptionist workspace (platform-level role, PRD-RECEPTIONIST.md)
const VrLayout = lazy(() => import("./components/VrLayout"));
const VrOverview = lazy(() => import("./pages/vr/VrOverview"));
const VrQueue = lazy(() => import("./pages/vr/VrQueue"));
const VrLeads = lazy(() => import("./pages/vr/VrLeads"));
const VrComms = lazy(() => import("./pages/vr/VrComms"));
const VrCalendar = lazy(() => import("./pages/vr/VrCalendar"));

// ════════════════════════════════════════════════════════════════════════════
// Appointment Flo - Med Spa Booking Platform
// ════════════════════════════════════════════════════════════════════════════

export default function App() {
  return (
    <ThemeProvider defaultTheme="system">
      <AuthProvider>
        <BrandingProvider>
          <ToastProvider>
            <BrowserRouter>
              {/* Impersonation banner (shows when super admin is impersonating) */}
              <ImpersonationBanner />

              <Suspense fallback={<DashboardSkeleton />}>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Home />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/book/:slug" element={<BookingWizard />} />
                  <Route path="/chat/:slug" element={<ChatPage />} />
                  <Route path="/form/:slug/:token" element={<PublicForm />} />
                  <Route path="/sign/:slug/:token" element={<PublicDocument />} />
                  <Route path="/rate/:slug/:token" element={<PublicRating />} />
                  <Route path="/review/:slug" element={<ReviewGate />} />
                  <Route path="/:slug/sms-terms" element={<SmsTerms />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/c/:tenantSlug/:campaignSlug" element={<PromoPage />} />
                  <Route path="/promo/:tenantSlug/:campaignSlug" element={<PromoPage />} />
                  <Route path="/book/:slug/manage/:code" element={<ManageAppointment />} />
                  <Route path="/accept-invite" element={<AcceptInvite />} />
                  <Route path="/reset-password" element={<ResetPassword />} />

                  {/* Onboarding Route */}
                  <Route path="/onboarding" element={<Onboarding />} />

                  {/* Flo AI Agent - full-page fallback (accessible but no longer default) */}
                  <Route path="/agent" element={<AgentChat />} />

                  {/* Tenant Dashboard Routes - wrapped in DashboardLayout for fluid sidebar */}
                  <Route path="/dashboard" element={<DashboardLayout><Dashboard /></DashboardLayout>} />
                  <Route path="/settings" element={<DashboardLayout><Settings /></DashboardLayout>} />
                  <Route path="/services" element={<DashboardLayout><Services /></DashboardLayout>} />
                  <Route path="/providers" element={<DashboardLayout><Providers /></DashboardLayout>} />
                  <Route path="/clients" element={<DashboardLayout><Clients /></DashboardLayout>} />
                  <Route path="/clients/:id" element={<DashboardLayout><ClientProfile /></DashboardLayout>} />
                  <Route path="/payments" element={<DashboardLayout><Payments /></DashboardLayout>} />
                  <Route path="/forms" element={<DashboardLayout><Forms /></DashboardLayout>} />
                  <Route path="/documents" element={<DashboardLayout><Documents /></DashboardLayout>} />
                  <Route path="/analytics" element={<DashboardLayout><Analytics /></DashboardLayout>} />
                  <Route path="/chatbot" element={<DashboardLayout><ChatbotAdmin /></DashboardLayout>} />
                  <Route path="/campaigns" element={<DashboardLayout><Campaigns /></DashboardLayout>} />
                  <Route path="/campaigns/leads/:leadId" element={<DashboardLayout><LeadProfile /></DashboardLayout>} />
                  <Route path="/campaigns/email-blasts" element={<DashboardLayout><EmailBlasts /></DashboardLayout>} />
                  <Route path="/campaigns/email-blasts/new" element={<DashboardLayout><EmailBlastEditor /></DashboardLayout>} />
                  <Route path="/campaigns/email-blasts/:id" element={<DashboardLayout><EmailBlastEditor /></DashboardLayout>} />
                  <Route path="/campaigns/:id" element={<DashboardLayout><CampaignDetail /></DashboardLayout>} />

                  {/* Virtual Receptionist Routes - simplified VR-only layout */}
                  <Route path="/vr" element={<VrLayout><VrOverview /></VrLayout>} />
                  <Route path="/vr/queue" element={<VrLayout><VrQueue /></VrLayout>} />
                  <Route path="/vr/leads" element={<VrLayout><VrLeads /></VrLayout>} />
                  <Route path="/vr/comms" element={<VrLayout><VrComms /></VrLayout>} />
                  <Route path="/vr/calendar" element={<VrLayout><VrCalendar /></VrLayout>} />

                  {/* Super Admin Routes - also use DashboardLayout */}
                  <Route path="/admin/dashboard" element={<DashboardLayout><AdminDashboard /></DashboardLayout>} />
                  <Route path="/admin/tenants" element={<DashboardLayout><AdminTenants /></DashboardLayout>} />
                  <Route path="/admin/chatbot" element={<DashboardLayout><AdminChatbotUsage /></DashboardLayout>} />
                  <Route path="/admin/users" element={<DashboardLayout><AdminUsers /></DashboardLayout>} />
                  <Route path="/admin/meta-leads" element={<DashboardLayout><AdminMetaLeads /></DashboardLayout>} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </ToastProvider>
        </BrandingProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

